import type { AdminRole, SystemSettings } from './models';

/** טיפוסי ה-API של מסך המפתח (/api/owner/*). */

export interface AdminListItem {
  uid: string;
  email: string;
  displayName: string;
  role: AdminRole;
  active: boolean;
  /** האם יש לחשבון סיסמה (אחרת צריך לשלוח קישור לבחירת סיסמה) */
  hasPassword: boolean | null;
  /** התחברות אחרונה (ISO), אם ידוע */
  lastLoginAt: string | null;
  /** מספר מכשירים שרשומים להתראות Push */
  pushDevices: number;
  createdAt: string | null;
}

export interface AdminsResponse {
  admins: AdminListItem[];
  max: number;
}

export interface AddAdminRequest {
  email: string;
  displayName: string;
}

export interface AddAdminResponse {
  uid: string;
  /** true אם המשתמש כבר היה קיים ב-Firebase Authentication */
  existingUser: boolean;
  /** קישור חד-פעמי לבחירת סיסמה — להעביר למנהל באופן אישי */
  passwordLink: string | null;
}

export type AdminAction = 'disable' | 'enable' | 'remove' | 'rename' | 'password_link';

export interface AdminActionRequest {
  action: AdminAction;
  displayName?: string;
}

export interface PasswordLinkResponse {
  passwordLink: string;
}

export interface SettingsResponse {
  settings: SystemSettings;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface HealthCheck {
  key: string;
  label: string;
  ok: boolean | null;
  detail: string;
}

export interface OwnerHealthResponse {
  version: string;
  serverTime: string;
  checks: HealthCheck[];
  counts: { activeJobs: number; archivedJobs: number; contractors: number; activeAdmins: number; errorsLast24h: number };
}

export interface ErrorLogItem {
  id: string;
  source: 'admin' | 'public' | 'server';
  message: string;
  detail: string;
  path: string;
  userAgent: string;
  uid: string | null;
  createdAt: string;
}

export interface ErrorsResponse {
  errors: ErrorLogItem[];
}

/** דיווח תקלה מהדפדפן (/api/errors) */
export interface ErrorReportRequest {
  source: 'admin' | 'public';
  message: string;
  detail?: string;
  path?: string;
}

export const ERROR_REPORT_LIMITS = { message: 300, detail: 2000, path: 200, userAgent: 300 } as const;

/** ניקוי וקיצור של דיווח תקלה לפני שמירה. לא שומרים נתונים אישיים מעבר לנתיב (בלי ?query). */
export function sanitizeErrorReport(input: Partial<ErrorReportRequest>, userAgent: string) {
  const clip = (v: unknown, max: number) => (typeof v === 'string' ? v.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '').slice(0, max) : '');
  const rawPath = clip(input.path, ERROR_REPORT_LIMITS.path);
  // בנתיב הדף האישי יש אסימון — מסתירים אותו
  const path = rawPath.split('?')[0]!.replace(/^\/c\/[A-Za-z0-9_-]+/, '/c/…');
  return {
    source: input.source === 'public' ? ('public' as const) : ('admin' as const),
    message: clip(input.message, ERROR_REPORT_LIMITS.message) || 'שגיאה לא ידועה',
    detail: clip(input.detail, ERROR_REPORT_LIMITS.detail).replace(/\/c\/[A-Za-z0-9_-]{20,}/g, '/c/…'),
    path,
    userAgent: clip(userAgent, ERROR_REPORT_LIMITS.userAgent),
  };
}
